import {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useEffect,
  ReactNode,
} from "react";
import api from "@/services/api";

type Lang = "en" | "fr";

interface TranslationContextValue {
  lang: Lang;
  translating: boolean;
  progress: number;
  total: number;
  toggleLang: () => void;
  t: (key: string) => string;
  retranslate: () => void;
  translateText: (text: string) => Promise<string>;
}

const TranslationContext = createContext<TranslationContextValue | null>(null);

const BATCH_SIZE = 20;
const BATCH_DELAY_MS = 500; // small gap between batches; server handles actual rate limiting
const CRAWL_INTERVAL_MS = 300; // check for new text nodes every 300ms
const MAX_CRAWL_ATTEMPTS = 20; // stop after 20 attempts (6 seconds total)

async function translateBatch(texts: string[], targetLang: Lang): Promise<string[]> {
  try {
    const res = await api.post("/translate/batch", {
      texts,
      target_lang: targetLang,
      source_lang: "auto",
    });
    const translations: { translated_text: string }[] = res.data.translations ?? [];
    return translations.map((t, i) => t.translated_text ?? texts[i]);
  } catch {
    return texts;
  }
}

function shouldTranslate(text: string): boolean {
  const t = text.trim();
  // Skip very short text (under 3 chars)
  if (!t || t.length < 3) return false;
  // Skip pure numbers, punctuation, symbols
  if (/^[\d\s\W]+$/.test(t)) return false;
  // Allow everything else - headings, hero text, single words, etc.
  return true;
}

function getTextNodes(root: Node): Text[] {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      const parent = node.parentElement;
      if (!parent) return NodeFilter.FILTER_REJECT;
      const tag = parent.tagName.toLowerCase();
      if (["script", "style", "noscript", "svg", "path"].includes(tag))
        return NodeFilter.FILTER_REJECT;
      if ((node.textContent ?? "").trim().length === 0)
        return NodeFilter.FILTER_REJECT;
      return NodeFilter.FILTER_ACCEPT;
    },
  });
  const nodes: Text[] = [];
  let node: Node | null;
  while ((node = walker.nextNode())) {
    nodes.push(node as Text);
  }
  return nodes;
}

export function TranslationProvider({ children }: { children: ReactNode }) {
  const [lang, setLang] = useState<Lang>("en");
  const [translating, setTranslating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [total, setTotal] = useState(0);
  const [staticTranslations, setStaticTranslations] = useState<Record<string, any>>({});
  const originals = useRef<Map<Text, string>>(new Map());
  const translations = useRef<Map<string, string>>(new Map()); // original -> translated
  const abortControllerRef = useRef<AbortController | null>(null);
  const isTranslated = useRef<boolean>(false);

  // Load static translations on mount
  useEffect(() => {
    const loadStaticTranslations = async () => {
      try {
        const en = await import("@/locales/en.json");
        const fr = await import("@/locales/fr.json");
        setStaticTranslations({ en: en.default, fr: fr.default });
      } catch (e) {
        console.error("Failed to load static translations:", e);
      }
    };
    loadStaticTranslations();
  }, []);

  // Helper function for static translations
  const t = useCallback((key: string): string => {
    const keys = key.split(".");
    let value: any = staticTranslations[lang];
    for (const k of keys) {
      value = value?.[k];
    }
    return value || key;
  }, [lang, staticTranslations]);

  // Load cached translations from localStorage on mount
  useEffect(() => {
    const cached = localStorage.getItem("translations_fr");
    if (cached) {
      try {
        translations.current = new Map(JSON.parse(cached));
      } catch {
        translations.current = new Map();
      }
    }
  }, []);

  // Save translations to localStorage when they change
  useEffect(() => {
    if (translations.current.size > 0) {
      localStorage.setItem("translations_fr", JSON.stringify([...translations.current]));
    }
  }, [lang]);

  const translatePage = useCallback(async (targetLang: Lang) => {
    // Cancel any ongoing translation
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    setTranslating(true);
    setProgress(0);

    const root = document.getElementById("root");
    if (!root) { setTranslating(false); return; }

    if (targetLang === "en") {
      const textNodes = getTextNodes(root);
      textNodes.forEach((node) => {
        const orig = originals.current.get(node);
        if (orig !== undefined) node.textContent = orig;
      });
      originals.current.clear();
      translations.current.clear();
      localStorage.removeItem("translations_fr");
      isTranslated.current = false;
      setTranslating(false);
      setProgress(0);
      return;
    }

    // Check if current page is static (has pre-saved translations)
    const currentPath = window.location.pathname;
    const staticPages = ["/", "/about", "/services", "/pricing", "/contact"];
    const isStaticPage = staticPages.includes(currentPath);

    // For static pages, don't use API translation - rely on JSON translations
    if (isStaticPage) {
      setTranslating(false);
      return;
    }

    // For dynamic pages (portfolio, projects), use API translation with continuous crawling
    let crawlAttempts = 0;
    let previousNodeCount = 0;
    const allTranslatableNodes: Text[] = [];

    // Continuous crawling loop
    while (crawlAttempts < MAX_CRAWL_ATTEMPTS) {
      if (abortControllerRef.current?.signal.aborted) {
        setTranslating(false);
        return;
      }

      const textNodes = getTextNodes(root);
      
      // Find new nodes that weren't in our previous scan
      const newNodes = textNodes.filter((node) => {
        const text = node.textContent ?? "";
        // Skip if already in our list
        if (allTranslatableNodes.includes(node)) return false;
        // Check if it's worth translating
        if (!shouldTranslate(text)) return false;
        // Save original if not already saved
        if (!originals.current.has(node)) {
          originals.current.set(node, text);
        }
        return true;
      });

      // Add new nodes to our list
      allTranslatableNodes.push(...newNodes);
      
      // Update total count if we found new nodes
      if (newNodes.length > 0) {
        setTotal(allTranslatableNodes.length);
      }

      // If no new nodes found, increment attempt counter
      if (newNodes.length === 0 && textNodes.length === previousNodeCount) {
        crawlAttempts++;
      } else {
        // Reset counter if we found new content
        crawlAttempts = 0;
        previousNodeCount = textNodes.length;
      }

      // Wait before next crawl
      if (crawlAttempts < MAX_CRAWL_ATTEMPTS) {
        await new Promise((r) => setTimeout(r, CRAWL_INTERVAL_MS));
      }
    }

    // Separate into cached and uncached texts
    const uncachedNodes: Text[] = [];
    const cachedTranslations: Map<Text, string> = new Map();

    allTranslatableNodes.forEach((node) => {
      const text = node.textContent ?? "";
      const cached = translations.current.get(text);
      if (cached) {
        cachedTranslations.set(node, cached);
        node.textContent = cached;
      } else {
        uncachedNodes.push(node);
      }
    });

    // Update progress for cached translations
    setProgress(cachedTranslations.size);

    // Only translate uncached texts
    for (let i = 0; i < uncachedNodes.length; i += BATCH_SIZE) {
      if (abortControllerRef.current?.signal.aborted) {
        setTranslating(false);
        return;
      }

      const batch = uncachedNodes.slice(i, i + BATCH_SIZE);
      const texts = batch.map((n) => n.textContent ?? "");
      const translated = await translateBatch(texts, targetLang);
      batch.forEach((node, idx) => {
        const original = texts[idx];
        const translation = translated[idx];
        translations.current.set(original, translation);
        node.textContent = translation;
      });
      setProgress(cachedTranslations.size + i + batch.length);

      if (i + BATCH_SIZE < uncachedNodes.length) {
        await new Promise((r) => setTimeout(r, BATCH_DELAY_MS));
      }
    }

    isTranslated.current = true;
    setTranslating(false);
    setProgress(0);
  }, []);

  const toggleLang = useCallback(() => {
    const next: Lang = lang === "en" ? "fr" : "en";
    setLang(next);
    translatePage(next);
  }, [lang, translatePage]);

  const retranslate = useCallback(() => {
    if (lang === "fr") {
      translatePage("fr");
    }
  }, [lang, translatePage]);

  const translateText = useCallback(async (text: string): Promise<string> => {
    if (lang === "en" || !text || text.trim().length < 3) {
      return text;
    }

    // Check cache first
    const cached = translations.current.get(text);
    if (cached) {
      return cached;
    }

    // Translate the text
    try {
      const translated = await translateBatch([text], "fr");
      const result = translated[0];
      translations.current.set(text, result);
      return result;
    } catch {
      return text;
    }
  }, [lang]);

  return (
    <TranslationContext.Provider value={{ lang, translating, progress, total, toggleLang, t, retranslate, translateText }}>
      {children}
    </TranslationContext.Provider>
  );
}

export function useTranslation() {
  const ctx = useContext(TranslationContext);
  if (!ctx) throw new Error("useTranslation must be used within TranslationProvider");
  return ctx;
}
